import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../api/supabase'
import { useAuth } from '../contexts/AuthContext'

export type POAStatus = 'active' | 'expiring_soon' | 'expired'

export interface POA {
  id: string
  importer_name: string
  importer_address: string | null
  importer_country: string | null
  eori_number: string | null
  vat_number: string | null
  signatory_name: string | null
  signed_date: string | null
  valid_until: string | null
  is_open_ended: boolean
  is_uk_local_company: boolean
  is_pva: boolean
  is_cds_approved: boolean
  agent_name: string | null
  notes: string | null
  drive_file_id: string | null
  drive_file_name: string | null
  drive_web_link: string | null
  ai_extracted: boolean
  ai_confidence: number | null
  created_by: string | null
  created_at: string
  updated_at: string
  status: POAStatus
  file_type: 'pdf' | 'image' | 'docx' | 'xlsx' | 'unknown'
  is_deleted: boolean
  deleted_at: string | null
  deleted_by: string | null
  deleted_by_email: string | null
  valid_until_estimated?: boolean
}

export type CreatePOAInput = {
  importer_name: string
  importer_address: string | null
  importer_country: string | null
  eori_number: string | null
  vat_number: string | null
  signatory_name: string | null
  signed_date: string | null
  valid_until: string | null
  is_open_ended: boolean
  is_uk_local_company: boolean
  is_pva: boolean
  is_cds_approved: boolean
  agent_name: string | null
  notes: string | null
  drive_file_id: string | null
  drive_file_name: string | null
  drive_web_link: string | null
  ai_extracted: boolean
  ai_confidence: number | null
  file_type?: 'pdf' | 'image' | 'docx' | 'xlsx' | 'unknown'
}
export type UpdatePOAInput = Partial<Omit<POA, 'id' | 'created_by' | 'created_at' | 'valid_until_estimated'>>

export interface POAStats {
  total: number
  active: number
  expiring_soon: number
  expired: number
}

function calculateStatus(poa: POA): { status: POAStatus; isEstimated: boolean } {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const soon = new Date(today)
  soon.setDate(today.getDate() + 30)

  if (poa.is_open_ended) {
    return { status: 'active', isEstimated: false }
  }

  let expiry: Date | null = null
  let isEstimated = false

  if (poa.valid_until) {
    expiry = new Date(poa.valid_until)
  } else if (poa.signed_date) {
    expiry = new Date(poa.signed_date)
    expiry.setFullYear(expiry.getFullYear() + 1)
    isEstimated = true
  }

  if (!expiry) {
    return { status: 'active', isEstimated: false }
  }

  const status: POAStatus = expiry < today ? 'expired' : expiry <= soon ? 'expiring_soon' : 'active'

  return { status, isEstimated }
}

export function usePOAs() {
  const { user } = useAuth()
  const [poas, setPOAs] = useState<POA[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchPOAs = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const { data, error: fetchError } = await supabase
        .from('poas')
        .select('*')

      if (fetchError) throw fetchError

      const enriched = (data ?? []).map(poa => {
        const { status, isEstimated } = calculateStatus(poa)
        return {
          ...poa,
          status,
          valid_until_estimated: isEstimated,
        }
      })

      setPOAs(enriched)

      // Fire background updates for records where calculated status differs from DB status
      enriched.forEach(poa => {
        const dbPoa = data?.find(d => d.id === poa.id)
        if (poa.status !== (dbPoa?.status ?? 'active')) {
          ;(async () => {
            try {
              await supabase
                .from('poas')
                .update({ status: poa.status })
                .eq('id', poa.id)
            } catch (e) {
              console.error('Failed to update POA status:', poa.id, e)
            }
          })()
        }
      })
    } catch (err) {
      console.error('Error fetching POAs:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch POAs')
      setPOAs([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchPOAs()
  }, [fetchPOAs])

  const stats: POAStats = {
    total: poas.length,
    active: poas.filter(p => p.status === 'active').length,
    expiring_soon: poas.filter(p => p.status === 'expiring_soon').length,
    expired: poas.filter(p => p.status === 'expired').length,
  }

  const createPOA = async (input: CreatePOAInput): Promise<POA> => {
    if (!user) throw new Error('Not authenticated')

    const { data, error: insertError } = await supabase
      .from('poas')
      .insert({
        ...input,
        created_by: user.id,
        file_type: input.file_type || 'pdf',
        is_deleted: false,
        deleted_at: null,
        deleted_by: null,
        deleted_by_email: null,
      })
      .select()
      .single()

    if (insertError) throw insertError

    await fetchPOAs()
    return data as POA
  }

  const updatePOA = async (id: string, input: UpdatePOAInput): Promise<POA> => {
    const { data, error: updateError } = await supabase
      .from('poas')
      .update({ ...input, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single()

    if (updateError) throw updateError

    await fetchPOAs()
    return data as POA
  }

  const softDeletePOA = async (id: string, userEmail: string): Promise<void> => {
    console.log('Starting softDeletePOA for id:', id, 'userEmail:', userEmail, 'userId:', user?.id)

    const now = new Date().toISOString()
    const updateData = {
      is_deleted: true,
      deleted_at: now,
      deleted_by: user?.id || null,
      deleted_by_email: userEmail,
    }

    console.log('Update data:', updateData)

    const { data, error: deleteError } = await supabase
      .from('poas')
      .update(updateData)
      .eq('id', id)
      .select()

    if (deleteError) {
      console.error('POA delete error:', deleteError)
      console.error('Error code:', deleteError.code)
      console.error('Error details:', deleteError.details)
      throw new Error(deleteError.message || 'Failed to delete POA')
    }

    console.log('Delete successful. Updated rows:', data)
    await fetchPOAs()
    console.log('List refreshed after delete')
  }

  // Z72: the entire point of soft-delete here is that a Drive-imported POA's drive_file_id
  // fingerprint survives deletion, so handleSyncFromDrive's "already imported" check (which
  // matches on drive_file_id regardless of is_deleted) keeps skipping it forever. A hard DELETE
  // destroys that fingerprint along with the row -- this was the actual bug behind "deleted POA
  // reappears on next sync": staff would soft-delete (correct, and by itself sufficient), then
  // also hit the ADMIN-only "permanently delete" in the Show Deleted view, which silently
  // reopened the door for Drive to re-import the same file as brand new. Refuse outright for any
  // row that still has a drive_file_id; hard-delete stays available for manually-created POAs
  // (drive_file_id null), which have no Drive fingerprint at stake.
  const hardDeletePOA = async (id: string): Promise<void> => {
    const target = poas.find(p => p.id === id)
    if (target?.drive_file_id) {
      throw new Error('This POA was imported from Google Drive -- permanently deleting it would let the next "Sync from Drive" re-import the same file. It stays out of the list as soft-deleted; delete the file from Drive itself if you need it gone from there too.')
    }
    const { error: deleteError } = await supabase
      .from('poas')
      .delete()
      .eq('id', id)
    if (deleteError) throw deleteError
    await fetchPOAs()
  }

  const restorePOA = async (id: string): Promise<void> => {
    const { error: updateError } = await supabase
      .from('poas')
      .update({
        is_deleted: false,
        deleted_at: null,
        deleted_by: null,
        deleted_by_email: null,
      })
      .eq('id', id)
    if (updateError) throw updateError
    await fetchPOAs()
  }

  const deletePOA = softDeletePOA

  async function importFromDrive(driveFile: import('../hooks/useGoogleDrive').DrivePOAFile, extracted: import('../api/claudePOAParser').POAExtracted) {
    const { data: { user } } = await supabase.auth.getUser()

    const mimeToFileType = (mimeType: string): 'pdf' | 'image' | 'docx' | 'xlsx' | 'unknown' => {
      if (mimeType === 'application/pdf') return 'pdf'
      if (mimeType.startsWith('image/')) return 'image'
      if (mimeType.includes('wordprocessingml')) return 'docx'
      if (mimeType.includes('spreadsheetml')) return 'xlsx'
      return 'unknown'
    }

    const { data, error } = await supabase
      .from('poas')
      .upsert({
        importer_name: extracted.importer_name,
        importer_address: extracted.importer_address,
        eori_number: extracted.eori_number,
        vat_number: extracted.vat_number,
        signatory_name: extracted.signatory_name,
        signed_date: extracted.signed_date,
        valid_until: extracted.valid_until,
        is_open_ended: extracted.is_open_ended,
        is_uk_local_company: extracted.is_uk_local_company,
        is_pva: extracted.is_pva,
        is_cds_approved: extracted.is_cds_approved,
        agent_name: driveFile.agentName,
        drive_file_id: driveFile.fileId,
        drive_file_name: driveFile.fileName,
        drive_web_link: driveFile.webViewLink,
        ai_extracted: true,
        ai_confidence: extracted.confidence,
        created_by: user?.id,
        file_type: mimeToFileType(driveFile.mimeType),
        is_deleted: false,
        deleted_at: null,
        deleted_by: null,
        deleted_by_email: null,
      }, { onConflict: 'drive_file_id' })
      .select().single()
    if (error) throw error
    await fetchPOAs()
    return data
  }

  return { poas, loading, error, stats, createPOA, updatePOA, deletePOA, softDeletePOA, hardDeletePOA, restorePOA, importFromDrive, refetch: fetchPOAs }
}
